import { ChevronDown, ChevronUp, Copy, Download, Trash2 } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { SERVER_URL } from "../../api/client";
import { fetchJson, sendJson } from "../../api/rest";
import type {
	SegmentDetail,
	SegmentListResult,
	SegmentSummary,
} from "../../types/planner";

const nf = new Intl.NumberFormat("da-DK");

const ErrorBanner = ({ message }: { message: string }) => (
	<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
		{message}
	</div>
);

const SegmentDiffPanel = ({ id }: { id: number }) => {
	const [detail, setDetail] = useState<SegmentDetail | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		// Re-runs the segment's own definition and diffs it against the stored
		// snapshot — product_facts is indexed for this, so it's fast enough to
		// do on every open rather than on a cron.
		fetchJson<SegmentDetail>(`/planner/segments/${id}`)
			.then((d) => {
				if (!cancelled) setDetail(d);
			})
			.catch((e: Error) => {
				if (!cancelled) setError(e.message);
			});
		return () => {
			cancelled = true;
		};
	}, [id]);

	if (error) return <ErrorBanner message={error} />;
	if (!detail)
		return <p className="px-4 py-3 text-sm text-slate-400">Loading diff…</p>;

	return (
		<div className="grid gap-4 px-4 py-3 sm:grid-cols-2">
			<div>
				<p className="mb-1 text-xs font-semibold text-success">
					Entered ({nf.format(detail.enteredCount)})
				</p>
				{detail.entered.length === 0 ? (
					<p className="text-xs text-slate-400">Nothing new.</p>
				) : (
					<ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-slate-600">
						{detail.entered.map((p) => (
							<li key={p.productId} className="truncate">
								{p.name}
							</li>
						))}
					</ul>
				)}
			</div>
			<div>
				<p className="mb-1 text-xs font-semibold text-danger">
					Left ({nf.format(detail.leftCount)})
				</p>
				{detail.left.length === 0 ? (
					<p className="text-xs text-slate-400">Nothing left.</p>
				) : (
					<ul className="max-h-40 space-y-0.5 overflow-y-auto text-xs text-slate-600">
						{detail.left.map((p) => (
							<li key={p.productId} className="truncate">
								{p.name}
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};

export const SegmentsTab = () => {
	const [result, setResult] = useState<SegmentListResult | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [expanded, setExpanded] = useState<number | null>(null);
	const [busyId, setBusyId] = useState<number | null>(null);

	const load = useCallback(async () => {
		const r = await fetchJson<SegmentListResult>(
			`/planner/segments?page=${page}&limit=25`,
		);
		setResult(r);
	}, [page]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		load()
			.then(() => {
				if (!cancelled) setError(null);
			})
			.catch((e: Error) => {
				if (cancelled) return;
				setResult(null);
				setError(e.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [load]);

	const duplicate = async (segment: SegmentSummary) => {
		setBusyId(segment.id);
		try {
			const detail = await fetchJson<SegmentDetail>(
				`/planner/segments/${segment.id}`,
			);
			await sendJson("/planner/segments", "POST", {
				name: `${segment.name} (copy)`,
				description: segment.description ?? undefined,
				definition: detail.definition,
			});
			await load();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusyId(null);
		}
	};

	const remove = async (segment: SegmentSummary) => {
		if (!window.confirm(`Delete segment "${segment.name}"?`)) return;
		setBusyId(segment.id);
		try {
			await sendJson(`/planner/segments/${segment.id}`, "DELETE");
			await load();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusyId(null);
		}
	};

	return (
		<div className="space-y-4">
			{error && <ErrorBanner message={error} />}

			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
							<tr>
								<th className="px-4 py-2.5 font-medium">Segment</th>
								<th className="px-4 py-2.5 font-medium">Rows</th>
								<th className="px-4 py-2.5 font-medium">Last run</th>
								<th className="px-4 py-2.5 font-medium">Updated by</th>
								<th className="px-4 py-2.5 font-medium" />
							</tr>
						</thead>
						<tbody>
							{loading && (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-slate-400"
									>
										Loading…
									</td>
								</tr>
							)}
							{!loading && result?.items.length === 0 && (
								<tr>
									<td
										colSpan={5}
										className="px-4 py-8 text-center text-slate-400"
									>
										No saved segments yet — build one in Catalogue and save it.
									</td>
								</tr>
							)}
							{result?.items.map((s) => (
								<Fragment key={s.id}>
									<tr className="border-t border-slate-100">
										<td className="px-4 py-3">
											<button
												type="button"
												onClick={() =>
													setExpanded((cur) => (cur === s.id ? null : s.id))
												}
												className="flex items-center gap-1.5 font-medium text-slate-700 hover:text-primary"
											>
												{expanded === s.id ? (
													<ChevronUp className="h-3.5 w-3.5" />
												) : (
													<ChevronDown className="h-3.5 w-3.5" />
												)}
												{s.name}
											</button>
											{s.description && (
												<p className="ml-5 text-xs text-slate-400">
													{s.description}
												</p>
											)}
										</td>
										<td className="px-4 py-3 tabular-nums text-slate-600">
											{s.lastRowCount != null ? nf.format(s.lastRowCount) : "—"}
										</td>
										<td className="px-4 py-3 text-slate-500">
											{s.lastRunAt
												? new Date(s.lastRunAt).toLocaleString("da-DK")
												: "never"}
										</td>
										<td className="px-4 py-3 text-slate-500">
											{s.updatedBy ?? "—"}
										</td>
										<td className="px-4 py-3">
											<div className="flex items-center justify-end gap-1">
												<a
													href={`${SERVER_URL}/planner/segments/${s.id}/export.csv`}
													className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-primary"
													title="Download CSV"
												>
													<Download className="h-4 w-4" />
												</a>
												<button
													type="button"
													disabled={busyId === s.id}
													onClick={() => duplicate(s)}
													className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-primary disabled:opacity-40"
													title="Duplicate"
												>
													<Copy className="h-4 w-4" />
												</button>
												<button
													type="button"
													disabled={busyId === s.id}
													onClick={() => remove(s)}
													className="rounded-lg p-1.5 text-slate-400 hover:bg-danger/10 hover:text-danger disabled:opacity-40"
													title="Delete"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										</td>
									</tr>
									{expanded === s.id && (
										<tr className="bg-slate-50/50">
											<td colSpan={5}>
												<SegmentDiffPanel id={s.id} />
											</td>
										</tr>
									)}
								</Fragment>
							))}
						</tbody>
					</table>
				</div>

				{result && result.totalPages > 1 && (
					<div className="flex items-center justify-between border-t border-slate-100 px-5 py-3">
						<p className="text-sm text-slate-500">
							{nf.format(result.total)} segment(s) · page {result.page} of{" "}
							{result.totalPages}
						</p>
						<div className="flex gap-2">
							<button
								type="button"
								disabled={result.page <= 1}
								onClick={() => setPage((p) => p - 1)}
								className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
							>
								Previous
							</button>
							<button
								type="button"
								disabled={result.page >= result.totalPages}
								onClick={() => setPage((p) => p + 1)}
								className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
